/**
 * NotificationBell Component
 *
 * Displays notification icon with badge counter and dropdown list
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Bell,
    BellRing,
    BellOff,
    MessageSquare,
    UserPlus,
    AlertTriangle,
    AtSign,
    CalendarClock,
    CalendarDays,
    Info,
    Megaphone,
    ShieldAlert,
    CheckCheck,
    Clock,
    Loader2,
    X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { useNotifications } from '@/contexts/NotificationContext';
import { useAuth } from '@/contexts/AuthContext';
import { useSidebar } from '@/components/internal/Sidebar';
import { AnimatedIcon, iconToneClass, useAnimatedIconState } from '@/components/internal/AnimatedIcon';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

// Notifications of the CRM agenda carry where a click lands in `metadata.url`.
const AGENDA_NOTIFICATION_TYPES = new Set(['CRM_ACTIVITY_REMINDER', 'CRM_ACTIVITY_DIGEST']);

// Only same-app paths ("/internal/...") may be navigated to from a notification payload.
const isInternalPath = (value) => typeof value === 'string' && value.startsWith('/') && !value.startsWith('//');

// Strip HTML tags for clean display
const stripHtml = (str) => (str || '').replace(/<[^>]*>/g, '').trim();

// Icon mapping for notification types
const NOTIFICATION_ICONS = {
    NEW_MESSAGE: MessageSquare,
    TICKET_ASSIGNED: UserPlus,
    NEW_TICKET: MessageSquare,
    SLA_WARNING: AlertTriangle,
    INACTIVITY_ALERT: Clock,
    MENTION: AtSign,
    PLATFORM_ALERT: Info,
    PLATFORM_BROADCAST: Megaphone,
    CRM_ACTIVITY_REMINDER: CalendarClock,
    CRM_ACTIVITY_DIGEST: CalendarDays,
    account_update: ShieldAlert,
};

// Color mapping for notification types
const NOTIFICATION_COLORS = {
    NEW_MESSAGE: 'text-blue-700 dark:text-blue-400',
    TICKET_ASSIGNED: 'text-green-700 dark:text-green-400',
    NEW_TICKET: 'text-cyan-700 dark:text-cyan-400',
    SLA_WARNING: 'text-yellow-700 dark:text-yellow-400',
    INACTIVITY_ALERT: 'text-orange-700 dark:text-orange-400',
    MENTION: 'text-purple-700 dark:text-purple-400',
    PLATFORM_ALERT: 'text-foreground-muted',
    PLATFORM_BROADCAST: 'text-indigo-700 dark:text-indigo-400',
    CRM_ACTIVITY_REMINDER: 'text-amber-700 dark:text-amber-400',
    CRM_ACTIVITY_DIGEST: 'text-sky-700 dark:text-sky-400',
    account_update: 'text-red-600 dark:text-red-400',
};

export function NotificationBell({ variant = 'icon' }) {
    const [isOpen, setIsOpen] = useState(false);
    let sidebarExpanded = true;
    try {
        const sidebar = useSidebar();
        sidebarExpanded = sidebar.expanded;
    } catch { /* not inside sidebar context */ }
    const {
        notifications,
        unreadCount,
        markAsRead,
        markAllAsRead,
        fetchNotifications,
        // Push notifications
        pushSupported,
        pushSubscribed,
        pushPermission,
        pushLoading,
        togglePush
    } = useNotifications();
    const { user } = useAuth();
    const navigate = useNavigate();

    // O sino do menu lateral toca ao passar o mouse e quando chega notificação nova.
    const { hovered, playSignal, replay, handlers: iconHandlers } = useAnimatedIconState(false);
    const lastUnread = useRef(unreadCount);
    useEffect(() => {
        if (unreadCount > lastUnread.current) replay();
        lastUnread.current = unreadCount;
    }, [unreadCount, replay]);

    // Fetch only unread notifications when popover opens
    useEffect(() => {
        if (isOpen) {
            fetchNotifications({ limit: 20, unreadOnly: true });
        }
    }, [isOpen, fetchNotifications]);

    const handleNotificationClick = (notification) => {
        // Mark as read
        if (!notification.readAt) {
            markAsRead(notification.id);
        }

        const metadata = notification.metadata || {};

        // Platform broadcast → navega para o viewer do comunicado
        if (notification.type === 'PLATFORM_BROADCAST') {
            const link = metadata.link || (metadata.broadcastId ? `/internal/comunicados/${metadata.broadcastId}` : null);
            if (link) {
                navigate(link);
                setIsOpen(false);
                return;
            }
        }

        // Lembrete de atividade e resumo diário do CRM → abrem o negócio (ou a gestão de
        // atividades). O caminho vem do servidor; só aceitamos caminho interno, nunca uma URL externa.
        if (AGENDA_NOTIFICATION_TYPES.has(notification.type) && isInternalPath(metadata.url)) {
            navigate(metadata.url);
            setIsOpen(false);
            return;
        }

        // Navigate to ticket if applicable
        if (notification.ticketId && user?.accountId) {
            navigate(`/internal/implementation/${user.accountId}/inbox?ticket=${notification.ticketId}`);
            setIsOpen(false);
        // Navigate to group if it's a group mention
        } else if (metadata.groupId && user?.accountId) {
            navigate(`/internal/implementation/${user.accountId}/inbox?tab=groups&group=${metadata.groupId}`);
            setIsOpen(false);
        }
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Agora';
        if (diffMins < 60) return `${diffMins}min`;
        if (diffHours < 24) return `${diffHours}h`;
        if (diffDays < 7) return `${diffDays}d`;
        return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
    };

    const sidebarTrigger = (
                    <div {...iconHandlers} className={cn(
                        "flex items-center rounded-lg px-3 py-2 cursor-pointer transition-colors relative",
                        "text-foreground-muted hover:bg-surface-hover/50 hover:text-foreground",
                        !sidebarExpanded && "justify-center px-2"
                    )}>
                        <AnimatedIcon
                            name="notifications"
                            fallback={Bell}
                            playSignal={playSignal}
                            colorKey={String(hovered)}
                            className={cn("h-5 w-5", iconToneClass({ hovered }))}
                        />
                        <span className={cn(
                            "font-medium transition-all duration-300 overflow-hidden whitespace-nowrap",
                            sidebarExpanded ? "opacity-100 max-w-full ml-3" : "opacity-0 max-w-0"
                        )}>
                            Notificações
                        </span>
                        {unreadCount > 0 && sidebarExpanded && (
                            <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                                {unreadCount > 9 ? '9+' : unreadCount}
                            </span>
                        )}
                        {unreadCount > 0 && !sidebarExpanded && (
                            <span className="absolute top-0.5 right-0.5 h-2.5 w-2.5 rounded-full bg-red-500" />
                        )}
                    </div>
    );

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
                {variant === 'sidebar' ? (
                    // A árvore é a mesma recolhido ou expandido (só o conteúdo do tooltip
                    // some): trocar o pai remontaria o gatilho e recarregaria o ícone animado.
                    <TooltipProvider delayDuration={100}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <PopoverTrigger asChild>
                                    {sidebarTrigger}
                                </PopoverTrigger>
                            </TooltipTrigger>
                            {!sidebarExpanded && (
                                <TooltipContent side="right" sideOffset={8} className="bg-surface border-border-strong text-foreground-strong z-[9999]">
                                    Notificações
                                </TooltipContent>
                            )}
                        </Tooltip>
                    </TooltipProvider>
                ) : (
                    <PopoverTrigger asChild>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="relative h-9 w-9 text-foreground-muted hover:text-foreground-strong hover:bg-surface-hover"
                    >
                        <Bell className="h-5 w-5" />
                        {unreadCount > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 flex h-5 w-5 items-center justify-center">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
                                <span className="relative inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                                    {unreadCount > 9 ? '9+' : unreadCount}
                                </span>
                            </span>
                        )}
                    </Button>
                    </PopoverTrigger>
                )}

            <PopoverContent
                side={variant === 'sidebar' ? 'right' : 'bottom'}
                align={variant === 'sidebar' ? 'start' : 'end'}
                sideOffset={variant === 'sidebar' ? 8 : 4}
                className="w-80 p-0 bg-surface border-border z-[9999]"
            >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                    <h3 className="font-semibold text-foreground-strong">Notificações</h3>
                    {unreadCount > 0 && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs text-foreground-muted hover:text-foreground-strong"
                            onClick={markAllAsRead}
                        >
                            <CheckCheck className="h-3.5 w-3.5 mr-1" />
                            Marcar todas como lidas
                        </Button>
                    )}
                </div>

                {/* Notification List */}
                <ScrollArea className="h-[400px]">
                    {notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-foreground-muted">
                            <Bell className="h-10 w-10 mb-3 opacity-50" />
                            <p className="text-sm">Nenhuma notificação</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-border">
                            {notifications.map((notification) => {
                                const Icon = NOTIFICATION_ICONS[notification.type] || Info;
                                const iconColor = NOTIFICATION_COLORS[notification.type] || 'text-foreground-muted';
                                const isUnread = !notification.readAt;

                                return (
                                    <div
                                        key={notification.id}
                                        className={cn(
                                            "flex items-start gap-3 p-4 cursor-pointer transition-colors group/notif relative",
                                            isUnread
                                                ? "bg-surface-active/50 hover:bg-surface-hover"
                                                : "hover:bg-surface-hover/30"
                                        )}
                                        onClick={() => handleNotificationClick(notification)}
                                    >
                                        {/* Icon */}
                                        <div className={cn(
                                            "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
                                            isUnread ? "bg-surface-active" : "bg-surface-active/50"
                                        )}>
                                            <Icon className={cn("h-4 w-4", iconColor)} />
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-2">
                                                <p className={cn(
                                                    "text-sm leading-tight",
                                                    isUnread ? "text-foreground-strong font-medium" : "text-foreground-secondary"
                                                )}>
                                                    {notification.title}
                                                </p>
                                                <span className="text-[10px] text-foreground-muted whitespace-nowrap">
                                                    {formatTime(notification.createdAt)}
                                                </span>
                                            </div>
                                            <p className="text-xs text-foreground-muted mt-1 line-clamp-2">
                                                {stripHtml(notification.body)}
                                            </p>
                                        </div>

                                        {/* Unread indicator + dismiss button */}
                                        <div className="flex-shrink-0 flex items-center gap-1 mt-1">
                                            {isUnread && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        markAsRead(notification.id);
                                                    }}
                                                    className="opacity-0 group-hover/notif:opacity-100 transition-opacity p-1 rounded hover:bg-border-strong text-foreground-muted hover:text-foreground-secondary"
                                                    title="Marcar como lida"
                                                >
                                                    <X className="h-3.5 w-3.5" />
                                                </button>
                                            )}
                                            {isUnread && (
                                                <div className="w-2 h-2 rounded-full bg-blue-500 group-hover/notif:hidden" />
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </ScrollArea>

                {/* Footer */}
                <div className="border-t border-border p-2 space-y-1">
                    {/* Push Notification Toggle */}
                    {pushSupported && (
                        <Button
                            variant="ghost"
                            className={cn(
                                "w-full text-sm justify-start gap-2",
                                pushSubscribed
                                    ? "text-green-700 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300"
                                    : "text-foreground-muted hover:text-foreground-strong"
                            )}
                            onClick={togglePush}
                            disabled={pushLoading || pushPermission === 'denied'}
                        >
                            {pushLoading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : pushSubscribed ? (
                                <BellRing className="h-4 w-4" />
                            ) : (
                                <BellOff className="h-4 w-4" />
                            )}
                            {pushPermission === 'denied'
                                ? 'Notificações bloqueadas'
                                : pushSubscribed
                                    ? 'Notificações ativadas'
                                    : 'Ativar notificações'}
                        </Button>
                    )}

                    {notifications.length > 0 && (
                        <Button
                            variant="ghost"
                            className="w-full text-sm text-foreground-muted hover:text-foreground-strong"
                            onClick={() => {
                                setIsOpen(false);
                            }}
                        >
                            Ver todas as notificações
                        </Button>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}

export default NotificationBell;
